// src/pages/team/TeamRolesPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import { getEntityId } from '@/features/team/logic';
import { RoleFormDialog } from '@/features/team/RoleFormDialog';
import { RoleIcon, PermissionsBadges, PERMISSION_GROUPS } from '@/features/team/RolePickers';
import { TeamSectionNav } from '@/features/team/TeamSectionNav';
import type { TeamPermission } from '@core/types/team/TeamPermission';

// ─── Role Request Dialog ─────────────────────────────────────────

function RoleRequestDialog({ teamId, role, onClose, onSuccess }: {
  teamId: string;
  role: { id: string; name: string };
  onClose: () => void;
  onSuccess: () => void;
}) {
  const store = useTeam();
  const { t } = useI18n();
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await store.CreateTeamRoleApplication(teamId, {
        application: {
          message: message.trim(),
          team: { id: teamId },
          requested_role: { id: role.id },
        } as never,
      });
      onSuccess();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('team.roleRequest.title')}
    >
      <button
        type="button"
        className="absolute inset-0 bg-foreground/50 backdrop-blur-sm cursor-default"
        aria-label={t('common.cancel')}
        onClick={onClose}
      />
      <Card className="relative w-full max-w-md animate-in fade-in zoom-in-95">
        <form onSubmit={handleSubmit}>
          <CardHeader>
            <CardTitle>{t('team.roleRequest.title')}</CardTitle>
            <CardDescription>{t('team.roleRequest.description', { role: role.name })}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {submitError != null && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}
            <Input
              label={t('team.roleRequest.messageLabel')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('team.roleRequest.messagePlaceholder')}
            />
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.loading') : t('team.roleRequest.submit')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}

// ─── Role Card ───────────────────────────────────────────────────

function RoleCard({ role, teamId, permissions, onDelete, onRequest, onTogglePerms, showPerms, t }: {
  role: any;
  teamId: string;
  permissions: TeamPermission[];
  onDelete: (id: string) => void;
  onRequest: (role: { id: string; name: string }) => void;
  onTogglePerms: (roleId: string) => void;
  showPerms: boolean;
  t: (key: string, params?: Record<string, string>) => string;
}) {
  const roleId = getEntityId(role);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="group relative rounded-xl border border-border bg-card hover:border-primary/30 hover:shadow-md transition-all">
      <div className="p-4">
        {/* Top section: icon + name + badges */}
        <div className="flex items-start gap-3">
          <RoleIcon icon={role.icon} color={role.color} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to={`/teams/${teamId}/roles/${roleId}`}
                className="text-sm font-semibold text-foreground hover:text-primary transition-colors truncate"
              >
                {role.name || '--'}
              </Link>
              {role.is_default && <Badge variant="primary" size="sm">{t('team.defaultRole')}</Badge>}
              {role.is_system && <Badge variant="secondary" size="sm">{t('team.systemRole')}</Badge>}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{role.description || t('team.noDescription')}</p>
          </div>
          <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{t('team.roleField.priority')}</span>
            <span
              className="text-xs font-bold rounded-full px-2.5 py-0.5"
              style={{
                backgroundColor: role.color ? `${role.color}15` : 'var(--muted)',
                color: role.color || 'var(--muted-foreground)',
              }}
            >
              {role.priority ?? 0}
            </span>
          </div>
        </div>

        {/* Permissions summary */}
        <div className="mt-3 pt-3 border-t border-border/50">
          <button
            type="button"
            className="flex items-center gap-2 w-full text-left group/perm"
            onClick={() => onTogglePerms(roleId)}
          >
            <svg
              className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${showPerms ? 'rotate-90' : ''}`}
              fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            <svg className="h-3.5 w-3.5 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span className="text-xs font-medium text-foreground">
              {t('team.permissions.title')}
            </span>
            <Badge variant={permissions.length > 0 ? 'primary' : 'secondary'} size="sm">
              {permissions.length}
            </Badge>
          </button>

          {!showPerms && permissions.length > 0 && (
            <div className="mt-2 ml-7">
              <PermissionsBadges permissions={permissions} t={t} />
            </div>
          )}
        </div>
      </div>

      {/* Expanded permissions view */}
      {showPerms && (
        <div className="border-t border-border bg-muted/20 px-4 py-3">
          {permissions.length === 0 ? (
            <p className="text-xs text-muted-foreground italic text-center py-2">{t('team.permissions.none')}</p>
          ) : (
            <div className="space-y-2">
              {PERMISSION_GROUPS.map((group) => {
                const groupPerms = permissions.filter(p => group.permissions.some(gp => gp.id === p.id));
                if (groupPerms.length === 0) return null;
                return (
                  <div key={group.key}>
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                      {t(`team.permissions.group.${group.key}`)}
                    </span>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {groupPerms.map((p) => (
                        <span key={p.id} className="text-[11px] px-2 py-0.5 rounded-md bg-primary/10 text-primary">
                          {t(`team.permissions.perm.${p.id}`)}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Bottom actions */}
      <div className="flex items-center justify-between px-4 py-2.5 border-t border-border/50">
        <div className="flex items-center gap-1">
          {role.color && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: role.color }} />
              <span className="font-mono">{role.color}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!role.is_system && !role.is_default && (
            <button
              type="button"
              onClick={() => onRequest({ id: roleId, name: role.name })}
              className="text-xs px-2.5 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              {t('team.roleRequest.button')}
            </button>
          )}
          <Link to={`/teams/${teamId}/roles/${roleId}`}>
            <button type="button" className="text-xs px-2.5 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              {t('common.edit')}
            </button>
          </Link>
          {!role.is_system && (
            confirmDelete ? (
              <div className="flex items-center gap-1 ml-1">
                <button type="button" onClick={() => onDelete(roleId)} className="text-xs px-2 py-1 rounded-md bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors">
                  {t('common.confirm')}
                </button>
                <button type="button" onClick={() => setConfirmDelete(false)} className="text-xs px-2 py-1 rounded-md text-muted-foreground hover:bg-muted transition-colors">
                  {t('common.cancel')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="text-xs px-2.5 py-1 rounded-md text-destructive/70 hover:text-destructive hover:bg-destructive/10 transition-colors"
              >
                {t('common.delete')}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────

export const TeamRolesPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTeam();
  const { t } = useI18n();

  const [roles, setRoles] = useState<any[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [requestRole, setRequestRole] = useState<{ id: string; name: string } | null>(null);
  const [expandedPerms, setExpandedPerms] = useState<string | null>(null);
  const [rolePermissions, setRolePermissions] = useState<Record<string, TeamPermission[]>>({});

  const loadRoles = useCallback(async () => {
    if (!id) return;
    try {
      const res = await store.ListTeamRoles(id);
      const loadedRoles = res?.roles?.roles ?? [];
      setRoles(loadedRoles);
      // Load permissions for all roles
      const permMap: Record<string, TeamPermission[]> = {};
      await Promise.all(loadedRoles.map(async (role: any) => {
        const roleId = getEntityId(role);
        if (!roleId) return;
        try {
          const permRes = await store.ListTeamPermissions(id, roleId);
          permMap[roleId] = permRes?.permissions?.permissions ?? [];
        } catch {
          permMap[roleId] = [];
        }
      }));
      setRolePermissions(permMap);
    } catch { /* store handles */ }
  }, [id, store]);

  useEffect(() => {
    if (id) store.GetTeam(id);
    loadRoles();
  }, [id, loadRoles]);

  const showSuccessMsg = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleDelete = async (roleId: string) => {
    if (!id) return;
    try {
      await store.DeleteTeamRole(id, roleId);
      showSuccessMsg(t('team.roleDeleted'));
      loadRoles();
    } catch { /* store handles */ }
  };

  const teamName = (store.currentItem as any)?.name || t('team.title');

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground transition-colors">{t('team.list')}</Link>
        <svg className="h-4 w-4 text-muted-foreground/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground transition-colors">{teamName}</Link>
        <svg className="h-4 w-4 text-muted-foreground/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        <span className="text-foreground font-medium">{t('team.roles')}</span>
      </nav>
      <TeamSectionNav teamId={id ?? ''} group="people" active="roles" />


      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('team.roles')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {roles.length > 0 ? `${roles.length} ${roles.length === 1 ? 'role' : 'roles'} defined` : t('team.noRoles')}
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          {t('team.createRole')}
        </Button>
      </div>

      {/* Alerts */}
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {successMessage && <Alert variant="success" dismissible onDismiss={() => setSuccessMessage(null)}>{successMessage as string}</Alert>}

      <RoleFormDialog
        open={showCreate}
        teamId={id!}
        onClose={() => setShowCreate(false)}
        onSaved={() => {
          showSuccessMsg(t('team.roleCreated'));
          loadRoles();
        }}
      />

      {/* Roles grid */}
      {store.isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-border p-4 animate-pulse">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-24 rounded bg-muted" />
                  <div className="h-3 w-40 rounded bg-muted" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : roles.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <svg className="h-8 w-8 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{t('team.noRoles')}</p>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              {t('team.createRole')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {roles.map((role: any) => {
            const roleId = getEntityId(role);
            return (
              <RoleCard
                key={roleId || Math.random()}
                role={role}
                teamId={id!}
                permissions={rolePermissions[roleId] ?? []}
                onDelete={handleDelete}
                onRequest={setRequestRole}
                onTogglePerms={(rid) => setExpandedPerms(expandedPerms === rid ? null : rid)}
                showPerms={expandedPerms === roleId}
                t={t}
              />
            );
          })}
        </div>
      )}

      {/* Role request dialog */}
      {requestRole && id && (
        <RoleRequestDialog
          teamId={id}
          role={requestRole}
          onClose={() => setRequestRole(null)}
          onSuccess={() => {
            setRequestRole(null);
            showSuccessMsg(t('team.roleRequest.sent'));
          }}
        />
      )}
    </div>
  );
});

export default TeamRolesPage;
